import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter, Redirect } from 'react-router';
import { injectStripe } from 'react-stripe-elements';
import * as reducers from 'Store/reducers';
import {
  enableFormReconciliationMode,
  setFormMessage,
  clearFormMessage,
  submitAddPaymentMethodForm,
} from 'Store/forms/actions';
import { fetchActiveUserIfNeeded, setAuthToken } from 'Store/users/actions';
import SectionHeader from 'Components/SectionHeader';
import Form from 'Components/Form';
import ConnectedFormattedMessage from 'Containers/ConnectedFormattedMessage';
import Loading from 'Components/Loading';
import PageMessage from 'Components/PageMessage';
import Button from 'Components/Button';
import { ROUTES } from 'Settings/route-strings';

export const FORM_ID = 'addPaymentMethod';

class AddPaymentMethodForm extends React.Component {
  constructor(props) {
    super(props);
    props.clearFormMessage(FORM_ID, FORM_ID);
    this.state = { isStripeLoading: false };
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleError = this.handleError.bind(this);
  }

  componentWillUnmount() {
    this.props.clearFormMessage(FORM_ID, FORM_ID);
  }

  handleError(error) {
    const { enableFormReconciliationMode, setFormMessage } = this.props;
    enableFormReconciliationMode(FORM_ID, FORM_ID);
    setFormMessage(FORM_ID, FORM_ID, {
      text: error.message || 'Error!',
      type: 'error',
      animationType: 'fade',
    });
  }

  async handleSubmit(fields, isFormValid) {
    const {
      clearFormMessage,
      enableFormReconciliationMode,
      stripe,
      schoolId,
      submitAddPaymentMethodForm,
    } = this.props;
    clearFormMessage(FORM_ID, FORM_ID);
    if (!isFormValid) {
      enableFormReconciliationMode(FORM_ID, FORM_ID);
      return;
    }

    this.setState({ isStripeLoading: true });
    const { source, error } = await stripe.createSource({
      type: 'card',
      owner: {
        name: fields.addPaymentMethod__cardholders_name.value,
      },
    });

    if (error) {
      this.setState({ isStripeLoading: false });
      this.handleError(error);
      return;
    }

    this.setState({ isStripeLoading: false });
    submitAddPaymentMethodForm(FORM_ID, FORM_ID, {
      stripeSourceId: source.id,
      schoolId,
    }).catch((e) => {
      this.handleError(e);
    });
  }

  render() {
    const { isStripeLoading } = this.state;
    const { form } = this.props;

    const { isPosting, message } = form || {};
    const loading = isStripeLoading || isPosting;

    const isSuccess = message && message.type === 'success';

    return (
      <React.Fragment>
        {isSuccess ? (
          <div style={{ width: '100%' }}>
            <PageMessage
              title="Success!"
              type="success"
              text="Your payment details have been saved."
            />
            <div style={{ maxWidth: 250, margin: '0 auto' }}>
              <Button
                buttonType="primary"
                size="small"
                label="Back to Settings"
                route={ROUTES.SETTINGS_SUBSCRIPTION}
              />
            </div>
          </div>
        ) : (
          <div style={{ width: '100%' }}>
            {loading && (
              <div className="PaymentLoading">
                <Loading />
              </div>
            )}

            <SectionHeader
              type="static-modal"
              title={
                <ConnectedFormattedMessage id="add_payment_method_form.title" />
              }
            />
            <Form
              type={FORM_ID}
              id={FORM_ID}
              messagePosition="content-frame"
              submitButtonType="secondary"
              formStyle="full-width"
              submitHandler={this.handleSubmit}
              isReadOnly={loading}
            />
          </div>
        )}
      </React.Fragment>
    );
  }
}

AddPaymentMethodForm.propTypes = {
  form: PropTypes.object,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
  enableFormReconciliationMode: PropTypes.func.isRequired,
  setFormMessage: PropTypes.func.isRequired,
  clearFormMessage: PropTypes.func.isRequired,
  stripe: PropTypes.shape({
    createSource: PropTypes.func.isRequired,
  }).isRequired,
};

const mapStateToProps = (state, { school }) => {
  return {
    authToken: reducers.getAuthToken(state),
    school: school || reducers.getActiveSchool(state),
    form: reducers.getForm(state, FORM_ID),
  };
};

const AddPaymentMethodFormWithStripe = withRouter(
  injectStripe(AddPaymentMethodForm)
);

export default connect(mapStateToProps, {
  enableFormReconciliationMode,
  submitAddPaymentMethodForm,
  setFormMessage,
  clearFormMessage,
  setAuthToken,
  fetchActiveUserIfNeeded,
})(AddPaymentMethodFormWithStripe);
